import { Link } from "@tanstack/react-router";
import type { BlogPost } from "@/lib/blog";
import { formatDate } from "./format";
import logo from "@/assets/mind-media-logo.svg";

function truncateToSentences(text: string, maxSentences = 3) {
  const sentences = text
    .replace(/([.!?])\s+/g, "$1|")
    .split("|")
    .filter((s) => s.trim().length > 0);
  const selected = sentences.slice(0, maxSentences);
  return selected.join(" ") + (sentences.length > maxSentences ? "..." : "");
}

export function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-md"
    >
      {post.cover ? (
        <div className="overflow-hidden bg-gray-100">
          <img
            src={post.cover}
            alt={post.title}
            className="w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-blue">
          {post.categoryLabel}
        </span>
        <h3 className="mt-2 font-display text-lg font-bold leading-snug text-gray-950">
          {post.title}
        </h3>
        {post.excerpt ? (
          <p className="mt-2 line-clamp-3 text-sm text-gray-600">{post.excerpt}</p>
        ) : null}
        <div className="mt-4 flex items-center gap-2 border-t border-black/5 pt-3">
          {post.author.avatar ? (
            <img
              src={post.author.avatar}
              alt=""
              className="h-6 w-6 rounded-full object-cover"
              loading="lazy"
            />
          ) : null}
          <span className="text-xs font-medium text-gray-700">{post.author.name}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500">{formatDate(post.date)}</span>
        </div>
      </div>
    </Link>
  );
}

export function FeaturedPostCard({ post }: { post: BlogPost }) {
  return (
    <div className="grid gap-6 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-black/5 sm:p-4 lg:grid-cols-2 lg:gap-10 lg:p-6">
        {post.cover ? (
          <Link
            to="/blog/$slug"
            params={{ slug: post.slug }}
            className="self-center overflow-hidden rounded-2xl"
          >
            <img src={post.cover} alt={post.title} className="w-full object-cover" />
          </Link>
        ) : (
          <Link
            to="/blog/$slug"
            params={{ slug: post.slug }}
            aria-label={post.title}
            className="relative flex aspect-[16/10] self-center items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-brand-blue via-purple-500 to-pink-500"
          >
            <img src={logo} alt="" className="h-12 w-auto opacity-90 [filter:brightness(0)_invert(1)] sm:h-16" />
          </Link>
        )}
      <div className="flex flex-col justify-center">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-brand-blue/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-blue">
          ★ Featured
        </span>
        <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-gray-950 sm:text-3xl lg:text-4xl">
          <Link
            to="/blog/$slug"
            params={{ slug: post.slug }}
            className="hover:text-brand-blue transition-colors"
          >
            {post.title}
          </Link>
        </h2>
        {post.excerpt ? (
          <p className="mt-3 text-base text-gray-600 sm:text-lg">
            {truncateToSentences(post.excerpt, 3)}
          </p>
        ) : null}
        <div className="mt-5 flex items-center gap-2.5">
          {post.author.avatar ? (
            <img
              src={post.author.avatar}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
              loading="lazy"
            />
          ) : null}
          <span className="text-sm font-medium text-gray-800">{post.author.name}</span>
          <span className="text-sm text-gray-400">·</span>
          <span className="text-sm text-gray-500">{formatDate(post.date)}</span>
        </div>
      </div>
    </div>
  );
}
